import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import DateTimePicker from "@/ui/internal/components/date-time/index";
import { combineDateAndTime } from "@utils/helper/index.helper";
import { format } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CricketMatchFetch,
  CricketMatchPost,
  CricketMatchUpdate,
} from "@services/CricketMatch";
import { toast } from "sonner";
import { usePathname } from "@utils/hook/index.hook";
import CricketFlag from "../../_components/CricketFlag";

// Zod validation schema
const addMatchSchema = z
  .object({
    cricket_team1: z.string().min(1, "Please select Team 1"),
    cricket_team2: z.string().min(1, "Please select Team 2"),
    stage: z.string().min(1, "Stage is required"),
    match_number: z.string().min(1, "Match Number is required"),
    match_start_time: z.date().nullable().optional(),
    match_end_time: z.date().nullable().optional(),
    status: z.string().min(1, "Please select a status"),
  })
  .refine((data) => data.cricket_team1 !== data.cricket_team2, {
    message: "Team 1 and Team 2 must be different",
    path: ["cricket_team2"],
  })
  .refine(
    (data) => {
      if (!data.match_start_time || !data.match_end_time) return true;
      return data.match_end_time > data.match_start_time;
    },
    {
      message: "End time must be after start time",
      path: ["match_end_time"],
    },
  );

type AddMatchFormData = z.infer<typeof addMatchSchema>;

const STATUSES = [
  { id: "prediction_open", name: "Prediction Open" },
  { id: "upcoming", name: "Upcoming" },
  { id: "prediction_closed", name: "Prediction Closed" },
];

export default function AddMatchForm({
  formdata = null,
  setOpen,
}: {
  formdata?: any;
  setOpen?: (e: boolean) => void;
}) {
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [startTime, setStartTime] = useState<any>("");
  const [endTime, setEndTime] = useState<any>("");
  const [startSchedule, setStartSchedule] = useState<any>([]);
  const [endSchedule, setEndSchedule] = useState<any>([]);
  const queryClient = useQueryClient();
  const pathname = usePathname();

  const { data, error } = useQuery({
    queryKey: ["cricket-match-fetch"],
    queryFn: () => CricketMatchFetch(),
  });

  const { mutate: MatchMutate } = useMutation({
    mutationFn: (data: any) => {
      if (formdata) {
        return CricketMatchUpdate(formdata?.id, { status: data?.status });
      } else {
        return CricketMatchPost(data);
      }
    },
    onSuccess: () => {
      toast.success("Match added successfully!");
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      if (setOpen) setOpen(false);
    },
    onError: (error) => {
      toast.error(error?.message);
    },
  });

  const teamData = data?.results?.map((e) => {
    return { id: e.id, name: e.name, iso_code: e.iso_code };
  });

  const handleStartSave = (updatedData: any) => {
    const combinedDateNTime = combineDateAndTime(
      updatedData[0].date,
      updatedData[0].startTime,
    );
    setStartSchedule(updatedData);
    setStartTime(combinedDateNTime);
    setValue("match_start_time", combinedDateNTime);
    setStartDateOpen(false);
  };

  const handleEndSave = (updatedData: any) => {
    const combinedDateNTime = combineDateAndTime(
      updatedData[0].date,
      updatedData[0].startTime,
    );
    setEndSchedule(updatedData);
    setEndTime(combinedDateNTime);
    setValue("match_end_time", combinedDateNTime);
    setEndDateOpen(false);
  };

  const {
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddMatchFormData>({
    resolver: zodResolver(addMatchSchema),
    defaultValues: {
      status: "",
      cricket_team1: "",
      cricket_team2: "",
      stage: "",
      match_number: "",
      match_start_time: undefined,
      match_end_time: undefined,
    },
  });

  console.log("These are the errors", errors);

  const onSubmit = async (data: AddMatchFormData) => {
    try {
      console.log("Form submitted:", data);
      MatchMutate(data);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  useEffect(() => {
    if (formdata) {
      // 1. Reset the standard React Hook Form fields
      reset({
        cricket_team1: formdata.cricket_team1.name.toLowerCase(),
        cricket_team2: formdata.cricket_team2.name.toLowerCase(),
        stage: formdata.stage,
        match_number: formdata.match_number,
        match_start_time: formdata.match_start_time
          ? new Date(formdata.match_start_time)
          : undefined,
        match_end_time: formdata.match_end_time
          ? new Date(formdata.match_end_time)
          : undefined,
        status: formdata.status,
      });

      // 2. Synchronize your local state variables used for the UI/Pickers
      setStartTime(
        formdata.match_start_time ? new Date(formdata.match_start_time) : "",
      );
      setEndTime(
        formdata.match_end_time ? new Date(formdata.match_end_time) : "",
      );

      // 3. Update the Schedule state so the DateTime component shows the existing values
      // Note: We format the date to YYYY-MM-DD to match your component's internal logic
      if (formdata.match_start_time) {
        const startObj = {
          date: format(new Date(formdata.match_start_time), "yyyy-MM-dd"),
          startTime: format(new Date(formdata.match_start_time), "hh:mm a"),
        };
        setStartSchedule([startObj]);
      } else {
        setStartSchedule([]);
      }

      if (formdata.match_end_time) {
        const endObj = {
          date: format(new Date(formdata.match_end_time), "yyyy-MM-dd"),
          startTime: format(new Date(formdata.match_end_time), "hh:mm a"),
        };
        setEndSchedule([endObj]);
      } else {
        setEndSchedule([]);
      }
    }
  }, [formdata, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-2xl">
      <div className="space-y-6">
        {/* Team 1 */}
        {!formdata && (
          <>
            <div className="space-y-2">
              <label htmlFor="team1" className="text-base font-medium">
                Team 1 <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control} // Get 'control' from useForm()
                name="cricket_team1"
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    key={field.value}
                  >
                    <SelectTrigger
                      className={
                        errors?.cricket_team1 ? "border-red-500" : "h-12"
                      }
                    >
                      <SelectValue placeholder="Choose Team 1" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamData?.map((team: any) => (
                        <SelectItem key={team?.id} value={team?.id?.toString()}>
                          <div className="flex items-center gap-2">
                            <CricketFlag
                              isoCode={team.iso_code}
                              className="w-5 h-5 shadow-sm rounded-sm"
                            />
                            <span>{team?.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors?.cricket_team1 && (
                <p className="text-sm text-red-500">
                  {errors?.cricket_team1?.message}
                </p>
              )}
            </div>

            {/* Team 2 */}
            <div className="space-y-2">
              <label htmlFor="team2" className="text-base font-medium">
                Team 2 <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control} // Get 'control' from useForm()
                name="cricket_team2"
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    key={field.value}
                  >
                    <SelectTrigger
                      className={
                        errors?.cricket_team2 ? "border-red-500" : "h-12"
                      }
                    >
                      <SelectValue placeholder="Choose Team 2" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamData?.map((team: any) => (
                        <SelectItem key={team?.id} value={team?.id?.toString()}>
                          <div className="flex items-center gap-2">
                            <CricketFlag
                              isoCode={team.iso_code}
                              className="w-5 h-5 shadow-sm rounded-sm"
                            />
                            <span>{team?.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors?.cricket_team2 && (
                <p className="text-sm text-red-500">
                  {errors?.cricket_team2?.message}
                </p>
              )}
            </div>

            {/* Stage and Match Number */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="stage" className="text-base font-medium">
                  Stage <span className="text-red-500">*</span>
                </label>
                <Controller
                  control={control}
                  name="stage"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="stage"
                      placeholder="e.g., Group z"
                      className={`${
                        errors?.stage ? "border-red-500" : ""
                      } h-12 text-base`}
                    />
                  )}
                />

                {errors?.stage && (
                  <p className="text-sm text-red-500">
                    {errors?.stage?.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="matchNumber" className="text-base font-medium">
                  Match Number <span className="text-red-500">*</span>
                </label>
                <Controller
                  control={control}
                  name="match_number"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="matchNumber"
                      placeholder="e.g., 1 out of 3"
                      className={`${
                        errors?.match_number ? "border-red-500" : ""
                      } h-12 text-base`}
                    />
                  )}
                />
                {errors?.match_number && (
                  <p className="text-sm text-red-500">
                    {errors?.match_number?.message}
                  </p>
                )}
              </div>
            </div>

            {/* Match Start Time */}
            <div className="space-y-2">
              <label htmlFor="matchStartTime" className="text-base font-medium">
                Match Start Time <span className="text-red-500">*</span>
              </label>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full h-12 justify-start text-left font-normal ${
                      errors?.match_start_time ? "border-red-500" : ""
                    }`}
                  >
                    {startTime
                      ? format(startTime, "dd MMM yyyy hh:mm a")
                      : "e.g., 26 Nov 2025 03:00 PM"}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="p-4">
                    <DateTimePicker
                      value={startSchedule} // Current value
                      dateTime={startSchedule} // Current state array
                      setDateTime={setStartSchedule} // State setter
                      onChange={handleStartSave} // Callback after clicking "Save"
                      isMulti={false} // Only one date/time pair
                    />
                  </div>
                </PopoverContent>
              </Popover>
              {errors?.match_start_time && (
                <p className="text-sm text-red-500">
                  {errors?.match_start_time?.message}
                </p>
              )}
            </div>

            {/* Match End Time */}
            <div className="space-y-2">
              <label htmlFor="matchEndTime" className="text-base font-medium">
                Match End Time <span className="text-red-500">*</span>
              </label>
              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full h-12 justify-start text-left font-normal ${
                      errors?.match_end_time ? "border-red-500" : ""
                    }`}
                  >
                    {endTime
                      ? format(endTime, "dd MMM yyyy hh:mm a")
                      : "e.g., 26 Nov 2025 03:00 PM"}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="p-4">
                    <DateTimePicker
                      value={endSchedule} // Current value
                      dateTime={endSchedule} // Current state array
                      setDateTime={setEndSchedule} // State setter
                      onChange={handleEndSave} // Callback after clicking "Save"
                      isMulti={false} // Only one date/time pair
                    />
                  </div>
                </PopoverContent>
              </Popover>
              {errors?.match_end_time && (
                <p className="text-sm text-red-500">
                  {errors?.match_end_time?.message}
                </p>
              )}
            </div>
          </>
        )}

        {/* Status */}
        <div className="space-y-2">
          <label htmlFor="status" className="text-base font-medium">
            Status <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={field.value}
                key={field.value}
              >
                <SelectTrigger
                  className={errors?.status ? "border-red-500" : "h-12"}
                >
                  <SelectValue placeholder="Choose Team 2" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((status) => (
                    <SelectItem key={status?.id} value={status?.id}>
                      {status?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors?.status && (
            <p className="text-sm text-red-500">{errors?.status?.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-center pt-6">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto px-8 h-12 bg-teal-600 hover:bg-teal-700 text-white text-base font-semibold rounded-lg"
          >
            {isSubmitting
              ? formdata
                ? "Updating Status..."
                : "Adding Match.."
              : formdata
                ? "Update Status"
                : "Add Match"}
          </Button>
        </div>
      </div>
    </form>
  );
}
